import React, { useState } from "react";
import { Image, View, StyleSheet, TouchableOpacity, Animated, Easing } from "react-native";
import { Plus, Loader2, Box } from "lucide-react";
import type { GetConnectorCatalogQuery } from "@axio/graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import INTEGRATIONS_ICONS, { type IntegrationIconKey } from "../../../../assets/integrations";
import { colors } from "../../../../styles/design-system";

type ServiceConnector = NonNullable<NonNullable<GetConnectorCatalogQuery["getConnectorCatalog"]["data"]>[number]>;

interface ServiceConnectorCardProps {
  connector: Pick<ServiceConnector, "id" | "name" | "icon" | "connectorType">;
  size?: number;
  onConnect: (connector: ServiceConnectorCardProps["connector"]) => void;
  isLoading?: boolean;
}

const LoadingSpinner = () => {
  const spinValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();
  }, [spinValue]);

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  return (
    <Animated.View style={{ transform: [{ rotate: spin }] }}>
      <Loader2 size={20} color="#7C87EA" strokeWidth={2} />
    </Animated.View>
  );
};

export const ServiceConnectorCard: React.FC<ServiceConnectorCardProps> = ({
  connector,
  size = 32,
  onConnect,
  isLoading = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const iconValue = connector.icon ? INTEGRATIONS_ICONS[connector.icon as IntegrationIconKey] : undefined;

  const cardStyles = [styles.connectorCard, isHovered && styles.connectorCardHovered];

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <TouchableOpacity
      style={cardStyles}
      onPress={() => onConnect(connector)}
      activeOpacity={0.7}
      disabled={isLoading}
      // @ts-expect-error - onMouseEnter/onMouseLeave are web-only props in RNW
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <View style={styles.cardInfo}>
        {iconValue ? (
          <Image
            source={{ uri: iconValue }}
            style={{
              width: size * 0.7,
              height: size * 0.7,
            }}
            resizeMode="contain"
          />
        ) : (
          <Box size={size * 0.7} color={colors.grey[400]} />
        )}
        <CustomSpacing size="m" />
        <View style={styles.nameContainer}>
          <CustomText style={styles.connectorName} text={connector.name} useTranslationText={false} />
        </View>
      </View>

      <View style={styles.cardAction}>
        {isLoading ? <LoadingSpinner /> : <Plus size={18} color={colors.grey[600]} />}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  connectorCard: {
    width: "48%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#eaecf0",
    backgroundColor: "#fff",
  },
  connectorCardHovered: {
    borderColor: colors.grey[400],
  },
  cardInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  nameContainer: {
    flex: 1,
    paddingHorizontal: 8,
  },
  connectorName: {
    fontSize: 15,
    fontWeight: "600",
    color: colors.grey[900],
  },
  cardAction: {
    marginLeft: 8,
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});
